// 多选下拉筛选（后台规范 §3.2，Cash 做法）：摘要「全部 / 单项名 / 已选 N 项」；面板 fixed 定位、可搜索、可取消全部；
// 同一时间只开一个，点外部 / Esc / 滚动 / 窗口变化都会关闭
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

export interface Option {
  value: string;
  label: string;
}

const OPEN_EVENT = "csp-ms-open";

export function MultiSelectFilter({
  label,
  options,
  value,
  onChange,
  testId,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (next: string[]) => void;
  testId?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [kw, setKw] = useState("");
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number }>({ left: 0 });
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  const picked = options.filter((o) => value.includes(o.value));
  const summary = picked.length === 0 ? "全部" : picked.length === 1 ? picked[0]!.label : `已选 ${picked.length} 项`;

  // 打开时计算位置：下方放不下就向上展开
  useLayoutEffect(() => {
    if (!open || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 32);
    const left = Math.max(16, Math.min(r.left, window.innerWidth - width - 16));
    const below = window.innerHeight - r.bottom;
    setPos(below < 320 && r.top > below ? { left, bottom: window.innerHeight - r.top + 4 } : { left, top: r.bottom + 4 });
  }, [open]);

  useEffect(() => {
    // 别的下拉打开时关掉自己
    const onOther = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) setOpen(false);
    };
    window.addEventListener(OPEN_EVENT, onOther);
    return () => window.removeEventListener(OPEN_EVENT, onOther);
  }, [id]);

  useEffect(() => {
    if (!open) return;
    const inside = (t: EventTarget | null) => !!t && ((panel.current?.contains(t as Node) ?? false) || (btn.current?.contains(t as Node) ?? false));
    const onDown = (e: MouseEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const toggleOpen = () => {
    if (!open) {
      setKw("");
      window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: id }));
    }
    setOpen(!open);
  };

  const list = options.filter((o) => !kw.trim() || o.label.toLowerCase().includes(kw.trim().toLowerCase()));
  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className="btn ms-trigger"
        onClick={toggleOpen}
        title={picked.length ? picked.map((p) => p.label).join("、") : undefined}
        aria-expanded={open}
        data-testid={testId}
      >
        <span className="muted">{label}：</span>
        <span>{summary}</span>
        <span className="muted">▾</span>
      </button>
      {open && (
        <div ref={panel} className="ms-panel" style={pos} role="dialog" aria-label={`${label}筛选`}>
          <input type="search" placeholder="搜索选项" value={kw} onChange={(e) => setKw(e.target.value)} autoFocus />
          <div className="row" style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-link small" onClick={() => onChange([])} disabled={!value.length}>
              取消全部选择
            </button>
          </div>
          <div className="ms-list">
            {list.length === 0 && <div className="muted small" style={{ padding: 6 }}>无匹配选项</div>}
            {list.map((o) => (
              <label key={o.value}>
                <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
